import {
  DEFAULT_HARDWARE_CHECKOUT_COUNTRY,
  isOtherShippingCountry,
} from "../../shippingCountries";
import type { PixiWizardSelection } from "../../hooks/usePixiWizardState";
import {
  BillingCycle,
  CurrencyCode,
  DealId,
  dealEligibility,
  DEFAULT_BILLING_CYCLE,
  DEFAULT_CURRENCY,
  DEFAULT_PLAN,
  PlanId,
} from "./constants";

export interface ResolvedSelection {
  planId: PlanId;
  currency: CurrencyCode;
  billingCycle: BillingCycle;
  shippingCountry: string;
  licenses: number;
}

/**
 * The single source of truth for the Plan & Billing selection: applies the
 * defaults and the Pro-trialist override (always Pro). Used by both the step
 * (cards/deals) and the wizard footer so they can never disagree.
 */
export const resolveSelection = (
  state: PixiWizardSelection,
  isProTrialist: boolean,
): ResolvedSelection => ({
  planId: isProTrialist ? "pro" : (state.planId ?? DEFAULT_PLAN),
  currency: state.currency ?? DEFAULT_CURRENCY,
  billingCycle: state.billingCycle ?? DEFAULT_BILLING_CYCLE,
  shippingCountry: state.shippingCountry ?? DEFAULT_HARDWARE_CHECKOUT_COUNTRY,
  licenses: state.screenCount,
});

/**
 * Whether the free-device deal can be chosen for the current selection: deals
 * must be available for the billing cycle (Yearly only) AND the "Ship to"
 * country must be one we can ship hardware to. Selecting the FE-only `Other`
 * option makes it unavailable (GRWBKK-1730).
 */
export const isFreeDeviceAvailable = (
  billingCycle: BillingCycle,
  shippingCountry: string,
): boolean =>
  dealEligibility(billingCycle).freeDeviceAvailable &&
  !isOtherShippingCountry(shippingCountry);

/**
 * The *valid* deal for the current selection — the single source of truth for
 * pricing, the footer total, deal highlighting and Continue gating, so every
 * consumer derives from a valid deal rather than the raw persisted `dealId`
 * (GRWBKK-1730). It only strips the one deal this feature invalidates: a
 * `free-device` selection whose "Ship to" country is the unsupported `Other`
 * option falls back to `discount` (always offered alongside free-device, i.e.
 * Yearly). Every other selection — including an unset deal, the discount deal
 * and the Monthly + discount pricing path (GRWBKK-1705) — is passed through
 * untouched.
 */
export const resolveEffectiveDealId = (
  state: PixiWizardSelection,
): DealId | undefined => {
  const shippingCountry =
    state.shippingCountry ?? DEFAULT_HARDWARE_CHECKOUT_COUNTRY;
  if (
    state.dealId === "free-device" &&
    isOtherShippingCountry(shippingCountry)
  ) {
    return "discount";
  }
  return state.dealId;
};
